// Whether a given row + field may be edited inline, and which field a Target Value save writes.
// Pure functions over OpportunityNode with no React and no design-system imports, so the branchy
// Target Value rules (spec 008 D5) are unit-tested without mounting anything — the same isolation
// opportunityTableRows.ts and opportunityFilters.ts keep.
import {hasPermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import type {OpportunityNode} from './opportunityTypes';

const TIME_AND_MATERIALS = 'TIME_AND_MATERIALS';

// Why a Target Value cell is read-only, so the cell can say so rather than just refusing to open.
export const TARGET_VALUE_READ_ONLY = {
	// Non-T&M rows display financialNumbers.baselineRevenue, a computed Financial Service number derived
	// from baseline roles and expenses. Nothing the client can write moves it: it is absent from every
	// GraphQL input and no mutation sets it. Writing the fixed price instead was measured against a live
	// stack and did not change it (budget 10,000 -> 25,000, baselineRevenue 0 throughout), so an editable
	// cell here would commit and visibly do nothing. Read-only with an explanation is the honest surface.
	CALCULATED: 'CALCULATED',
	// A T&M project not using a baseline has no baselineTarget to edit. Kept apart from the missing
	// end date below because they are different problems with different answers - telling someone to
	// set an end date when the real cause is the baseline toggle sends them to the wrong place.
	NO_BASELINE: 'NO_BASELINE',
	// T&M with a baseline but no end date: the same gate the Create Opportunity modal applies in
	// targetValueFieldState. baselineTarget needs an end date to mean anything.
	NO_END_DATE: 'NO_END_DATE',
} as const;

export type TargetValueReadOnly = (typeof TARGET_VALUE_READ_ONLY)[keyof typeof TARGET_VALUE_READ_ONLY];

export type TargetValueEdit = {editable: true} | {editable: false; reason: TargetValueReadOnly};

/**
 * The one gate for every inline editor (D4): the same permission ProjectLogic.updateProjectById
 * validates. Without it the cells render exactly as they do today — no pointer, no hover tint, no
 * click target — instead of offering an edit the backend would reject.
 */
export const canEditRow = (): boolean => hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE);

/**
 * Whether THIS row may be edited. The permission above is company-wide and says nothing about access to
 * an individual project, so a project the viewer holds read-only stays read-only however broad their
 * permissions are - the same check the expense list makes before letting a row be edited.
 */
export const canEditOpportunity = (node: OpportunityNode): boolean => canEditRow() && !node.readOnlyAccess;

const hasEndDate = (node: OpportunityNode): boolean => !!(node.projectEndYear && node.projectEndMonth && node.projectEndDay);

/**
 * Whether Target Value is editable on this row.
 *
 * Only T&M is editable, and only it writes anything: the column displays baselineTarget for T&M and
 * baselineRevenue for everything else, and only baselineTarget is writable. The display split is the
 * requirement, so the editable set follows it rather than the other way round.
 */
export const targetValueEdit = (node: OpportunityNode): TargetValueEdit => {
	if (node.budgetType !== TIME_AND_MATERIALS) {
		return {editable: false, reason: TARGET_VALUE_READ_ONLY.CALCULATED};
	}
	if (!node.useBaseline) {
		return {editable: false, reason: TARGET_VALUE_READ_ONLY.NO_BASELINE};
	}
	if (!hasEndDate(node)) {
		return {editable: false, reason: TARGET_VALUE_READ_ONLY.NO_END_DATE};
	}
	return {editable: true};
};

/** The stored value the Target Value editor pre-fills with — always baselineTarget, the only writable one. */
export const targetValueCurrent = (node: OpportunityNode): number | null => node.baselineTarget ?? null;
